import * as React from 'react'

export interface LoadingState {
  id: string
  message: string
  priority?: number
  timestamp: number
}

interface Props {
  loadingStates: Map<string, LoadingState>
  startLoading: (id: string, message: string, priority?: number) => void
  updateLoadingMessage: (id: string, message: string) => void
  stopLoading: (id: string) => void
  isLoading: (id?: string) => boolean
  getCurrentMessage: () => string | undefined
}

const LoadingContext = React.createContext<Props | null>(null)

interface ProviderProps {
  children: React.ReactNode
}

export const LoadingProvider = ({
  children
}: ProviderProps): JSX.Element => {
  const [loadingStates, setLoadingStates] = React.useState<Map<string, LoadingState>>(new Map())

  const startLoading = React.useCallback((id: string, message: string, priority = 0) => {
    setLoadingStates(prev => {
      const next = new Map(prev)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      next.set(id, { id, message, priority, timestamp: Date.now() })
      return next
    })
  }, [])

  const updateLoadingMessage = React.useCallback((id: string, message: string) => {
    setLoadingStates(prev => {
      const next = new Map(prev)
      const current = next.get(id)
      if (current) {
        next.set(id, { ...current, message })
      }
      return next
    })
  }, [])

  const stopLoading = React.useCallback((id: string) => {
    setLoadingStates(prev => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  const isLoading = React.useCallback((id?: string) => {
    if (id) {
      return loadingStates.has(id)
    }
    return loadingStates.size > 0
  }, [loadingStates])

  const getCurrentMessage = React.useCallback(() => {
    if (loadingStates.size === 0) return undefined
    return Array.from(loadingStates.values())
      .sort((a, b) => {
        const priorityDiff = (b.priority ?? 0) - (a.priority ?? 0)
        if (priorityDiff !== 0) return priorityDiff
        return b.timestamp - a.timestamp
      })[0]?.message
  }, [loadingStates])

  const value = React.useMemo(() => ({
    loadingStates,
    startLoading,
    updateLoadingMessage,
    stopLoading,
    isLoading,
    getCurrentMessage,
  }), [loadingStates, startLoading, updateLoadingMessage, stopLoading, isLoading, getCurrentMessage])

  return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
}

export const useLoadingContext = () => {
  const context = React.useContext(LoadingContext)
  if (!context) {
    throw new Error('useLoadingContext must be used within a LoadingProvider')
  }
  return context
}

export default LoadingContext
